/**
 * ExportModal Component
 * Validates and displays the exact JSON export contract for Chord Notator
 * Provides one-click JSON copy and Standard MIDI File (.mid) download
 */

import React, { useState } from 'react';
import { Copy, Check, Download, X, FileText, CheckCircle2, Printer, Music2 } from 'lucide-react';
import { MidiExportData } from '../types/midi';
import { downloadMidiFile } from '../core/midiFileExport';
import { downloadMusicXMLFile } from '../core/scoreTranscriber';
import { getTicksPerMeasure } from '../core/timing';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportData: MidiExportData;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  exportData,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const jsonString = JSON.stringify(exportData, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleDownloadMidi = () => {
    downloadMidiFile(exportData, `recording-bpm${exportData.bpm}.mid`);
  };

  const handleDownloadMusicXML = () => {
    downloadMusicXMLFile(
      exportData.notes,
      exportData.timeSignature,
      exportData.ppq,
      exportData.bpm,
      'C',
      60,
      `score-export-${Date.now()}.musicxml`
    );
  };

  const handlePrintScore = () => {
    window.print();
  };

  // Calculate notes distribution by measure according to §3.3
  const measureTicks = getTicksPerMeasure(exportData.timeSignature, exportData.ppq);
  const notesByMeasure: Record<number, number> = {};
  for (const n of exportData.notes) {
    const measureIndex = Math.floor(n.startTick / measureTicks);
    notesByMeasure[measureIndex] = (notesByMeasure[measureIndex] || 0) + 1;
  }

  return (
    <div
      id="export-modal-backdrop"
      className="fixed inset-0 z-50 bg-[#1B1B1B]/60 backdrop-blur-xs flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        id="export-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FFFFFF] border-2 border-[#1B1B1B] shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden text-[#1B1B1B]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#1B1B1B] bg-[#F8F7F4]">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2563eb]" />
            <div>
              <h2 className="text-base font-bold text-[#1B1B1B]">MIDI Export & Chord Notator Contract</h2>
              <p className="text-xs text-[#1B1B1B]/60">Strict §3.2 JSON format + Standard MIDI File (.mid)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[#1B1B1B]/60 hover:text-[#1B1B1B] hover:bg-[#F2F0EB] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-[#F8F7F4] p-2.5 border border-[#1B1B1B]/20">
              <span className="text-[#1B1B1B]/60 block text-[10px] uppercase font-bold">Notes Total</span>
              <span className="text-lg font-bold text-[#2563eb]">{exportData.notes.length}</span>
            </div>
            <div className="bg-[#F8F7F4] p-2.5 border border-[#1B1B1B]/20">
              <span className="text-[#1B1B1B]/60 block text-[10px] uppercase font-bold">Chart Chords</span>
              <span className="text-lg font-bold text-emerald-600">
                {exportData.chords?.length ?? 0}
              </span>
            </div>
            <div className="bg-[#F8F7F4] p-2.5 border border-[#1B1B1B]/20">
              <span className="text-[#1B1B1B]/60 block text-[10px] uppercase font-bold">Tempo & Grid</span>
              <span className="text-sm font-semibold text-[#1B1B1B]">
                {exportData.bpm} BPM &bull; {exportData.quantizeGrid}
              </span>
            </div>
            <div className="bg-[#F8F7F4] p-2.5 border border-[#1B1B1B]/20">
              <span className="text-[#1B1B1B]/60 block text-[10px] uppercase font-bold">Time Signature</span>
              <span className="text-sm font-semibold text-[#1B1B1B]">
                {exportData.timeSignature.beats}/{exportData.timeSignature.beatType} (PPQ {exportData.ppq})
              </span>
            </div>
          </div>

          {/* §3.3 Measure mapping check */}
          <div className="bg-[#F8F7F4] p-3 border border-[#1B1B1B]/20 text-xs">
            <div className="flex items-center gap-1.5 text-emerald-700 font-bold mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span>Chord Notator Measure Alignment (§3.3)</span>
            </div>
            <p className="text-[#1B1B1B]/70 text-[11px] leading-relaxed">
              Formula: <code className="bg-[#FFFFFF] px-1 py-0.5 border border-[#1B1B1B]/20 text-[#1B1B1B] font-mono">floor(startTick / {measureTicks})</code> matches index in <code className="bg-[#FFFFFF] px-1 py-0.5 border border-[#1B1B1B]/20 text-[#1B1B1B] font-mono">sourceChart: Measure[]</code>.
            </p>
          </div>

          {/* JSON Code Viewer */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-[#1B1B1B]">JSON Payload</span>
              <span className="text-[11px] text-[#1B1B1B]/60 font-mono">application/json</span>
            </div>
            <div className="relative">
              <pre className="bg-[#F8F7F4] p-3.5 border border-[#1B1B1B]/20 text-[11px] font-mono text-[#1B1B1B] max-h-56 overflow-auto leading-tight">
                {jsonString}
              </pre>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-wrap items-center justify-between px-5 py-3 border-t-2 border-[#1B1B1B] bg-[#F8F7F4] gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleDownloadMidi}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs font-bold transition-all shadow-xs"
              title="Download standard MIDI file (.mid)"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download MIDI</span>
            </button>

            <button
              onClick={handleDownloadMusicXML}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#F2F0EB] text-[#1B1B1B] border border-[#1B1B1B]/30 text-xs font-bold transition-all shadow-xs"
              title="Export sheet music in MusicXML format for MuseScore, Sibelius, Dorico"
            >
              <Music2 className="w-3.5 h-3.5 text-[#2563eb]" />
              <span>MusicXML</span>
            </button>

            <button
              onClick={handlePrintScore}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#F2F0EB] text-[#1B1B1B] border border-[#1B1B1B]/30 text-xs font-bold transition-all shadow-xs"
              title="Print sheet music or save as PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print / PDF</span>
            </button>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-[#1B1B1B]/70 hover:text-[#1B1B1B] text-xs hover:bg-[#F2F0EB] transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold transition-all border ${
                copied
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'bg-[#1B1B1B] border-[#1B1B1B] text-white hover:bg-black'
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span>{copied ? 'Copied!' : 'Copy JSON'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
